import { useEffect, useState } from 'react';
import { api, type Employee, type Station } from '../lib/api.ts';
import AdminCzasy from './AdminCzasy.tsx';
import { Odznaka, Pusto, Tabela, Zakladki, type Kolumna } from '../ui/index.tsx';

/** Panel administratora: pracownicy, czasy normatywne, historia importów danych. */
type Widok = 'ludzie' | 'czasy' | 'importy';

export default function Admin() {
  const [widok, setWidok] = useState<Widok>('ludzie');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-head-sm">Panel administratora</h1>
        <div className="ml-auto">
          <Zakladki<Widok>
            wartosc={widok}
            onZmiana={setWidok}
            opcje={[['ludzie', 'Pracownicy'], ['czasy', 'Czasy normatywne'], ['importy', 'Importy danych']]}
          />
        </div>
      </div>

      {widok === 'ludzie' && <Ludzie />}
      {widok === 'czasy' && <AdminCzasy />}
      {widok === 'importy' && <Importy />}
    </div>
  );
}

// ---------------------------------------------------------------- pracownicy

function Ludzie() {
  const [rows, setRows] = useState<Employee[]>([]);
  const [wgLokalizacji, setWgLokalizacji] = useState<Array<{ location: string; ile: number }>>([]);
  const [lokalizacja, setLokalizacja] = useState('A4');
  const [stations, setStations] = useState<Station[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [laduje, setLaduje] = useState(true);
  const [dodaje, setDodaje] = useState(false);
  const [draft, setDraft] = useState({ login: '', employee_code: '', station_code: '', location: 'A4' });

  const load = () => {
    setLaduje(true);
    return api.get<{ employees: Employee[]; wgLokalizacji: Array<{ location: string; ile: number }> }>(
      `/admin/employees${lokalizacja ? `?lokalizacja=${lokalizacja}` : ''}`,
    ).then((r) => { setRows(r.employees); setWgLokalizacji(r.wgLokalizacji); })
     .finally(() => setLaduje(false));
  };
  useEffect(() => { load(); }, [lokalizacja]);
  useEffect(() => { api.get<{ stations: Station[] }>('/catalog').then((r) => setStations(r.stations)); }, []);

  const ustawPin = async (e: Employee) => {
    const pin = prompt(`Nowy PIN dla ${e.login}. Od 4 do 8 cyfr:`);
    if (!pin) return;
    try {
      await api.post(`/admin/employees/${e.id}/pin`, { pin });
      setMsg(`PIN dla ${e.login} ustawiony. Otwarte sesje tego pracownika zostały zamknięte.`);
      load();
    } catch (err: any) { setMsg(err.message); }
  };

  const przelacz = async (e: Employee) => {
    await api.patch(`/admin/employees/${e.id}`, { is_active: !e.is_active });
    load();
  };

  const dodaj = async () => {
    try {
      await api.post('/admin/employees', {
        login: draft.login,
        employee_code: draft.employee_code || null,
        station_code: draft.station_code || null,
        location: draft.location,
      });
      setDraft({ login: '', employee_code: '', station_code: '', location: 'A4' });
      setDodaje(false);
      setMsg('Dodano pracownika. Nadaj mu PIN, żeby mógł się zalogować.');
      load();
    } catch (err: any) { setMsg(err.message); }
  };

  const kolumny: Array<Kolumna<Employee>> = [
    { klucz: 'login', naglowek: 'Login', sort: (e) => e.login, szer: '110px',
      render: (e) => (
        <span className={`font-semibold tnum ${e.is_active ? 'text-plum' : 'text-plum-muted line-through'}`}>
          {e.login}
        </span>
      ) },
    { klucz: 'lokalizacja', naglowek: 'Lokalizacja', sort: (e) => e.location, szer: '110px',
      render: (e) => <Odznaka ton={e.location === 'A4' ? 'neutral' : 'warn'}>{e.location}</Odznaka> },
    { klucz: 'strefa', naglowek: 'Strefa', sort: (e) => e.zone ?? '',
      render: (e) => <span className="text-plum-soft">{e.zone ?? '—'}</span> },
    { klucz: 'rola', naglowek: 'Rola', sort: (e) => e.role, szer: '100px',
      render: (e) => <span className="text-plum-soft">{e.role}</span> },
    { klucz: 'pin', naglowek: 'PIN', szer: '92px',
      render: (e) => e.has_pin ? <Odznaka ton="ok">nadany</Odznaka> : <Odznaka ton="warn">brak</Odznaka> },
    { klucz: 'akcje', naglowek: '', prawo: true, szer: '190px',
      render: (e) => (
        <span className="whitespace-nowrap">
          <button className="btn-quiet !min-h-0 !py-1 !px-2 text-caption" onClick={() => ustawPin(e)}>
            {e.has_pin ? 'Zmień PIN' : 'Nadaj PIN'}
          </button>
          <button className="btn-quiet !min-h-0 !py-1 !px-2 text-caption hover:!text-bad ml-1"
                  onClick={() => przelacz(e)}>
            {e.is_active ? 'Dezaktywuj' : 'Przywróć'}
          </button>
        </span>
      ) },
  ];

  const aktywni = rows.filter((r) => r.is_active).length;
  const bezPinu = rows.filter((r) => !r.has_pin && r.is_active).length;

  return (
    <>
      {msg && (
        <p className="card p-3 text-body-sm text-plum-soft" role="status">{msg}</p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select className="field !w-auto" value={lokalizacja} onChange={(e) => setLokalizacja(e.target.value)}
                aria-label="Lokalizacja">
          <option value="A4">Lokalizacja A4</option>
          <option value="S8">Lokalizacja S8</option>
          <option value="">Wszystkie lokalizacje</option>
        </select>
        <span className="text-body-sm text-plum-soft">
          {aktywni} aktywnych, {bezPinu} bez PIN-u
          {wgLokalizacji.length > 1 && (
            <span className="text-plum-muted"> · w bazie: {wgLokalizacji.map((w) => `${w.location} ${w.ile}`).join(', ')}</span>
          )}
        </span>
        <button className="btn-primary ml-auto" onClick={() => setDodaje((v) => !v)}>
          {dodaje ? 'Anuluj' : 'Dodaj pracownika'}
        </button>
      </div>

      {dodaje && (
        <div className="card p-5 grid sm:grid-cols-4 gap-3">
          <div>
            <label className="label" htmlFor="l">Login</label>
            <input id="l" className="field uppercase tnum" value={draft.login}
                   onChange={(e) => setDraft({ ...draft, login: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <label className="label" htmlFor="k">Kod kadrowy</label>
            <input id="k" className="field tnum" value={draft.employee_code}
                   onChange={(e) => setDraft({ ...draft, employee_code: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="label" htmlFor="s">Domyślne stanowisko</label>
            <select id="s" className="field" value={draft.station_code}
                    onChange={(e) => setDraft({ ...draft, station_code: e.target.value })}>
              <option value="">— brak —</option>
              {stations.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="loc">Lokalizacja</label>
            <select id="loc" className="field" value={draft.location}
                    onChange={(e) => setDraft({ ...draft, location: e.target.value })}>
              <option value="A4">A4</option>
              <option value="S8">S8</option>
            </select>
          </div>
          <div className="sm:col-span-2 flex items-end">
            <p className="text-caption text-plum-muted pb-2.5">
              Pracownika identyfikuje wyłącznie login. Aplikacja nie przechowuje imion ani nazwisk.
            </p>
          </div>
          <div className="flex items-end">
            <button className="btn-primary w-full" disabled={!draft.login} onClick={dodaj}>Zapisz</button>
          </div>
        </div>
      )}

      <Tabela
        dane={rows}
        kolumny={kolumny}
        klucz={(e) => e.id}
        laduje={laduje}
        pusto={<Pusto tytul="Brak pracowników w tej lokalizacji"
                      opis="Zmień filtr lokalizacji albo dodaj pierwszego pracownika." />}
      />
    </>
  );
}

// ---------------------------------------------------------------- importy

interface ImportRow {
  id: number; kind: string; source: string | null;
  rows_read: number; rows_added: number; status: string;
  message: string | null; created_at: string;
}

function Importy() {
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [laduje, setLaduje] = useState(true);

  useEffect(() => {
    api.get<{ imports: ImportRow[] }>('/admin/imports')
      .then((r) => setRows(r.imports))
      .finally(() => setLaduje(false));
  }, []);

  const kolumny: Array<Kolumna<ImportRow>> = [
    { klucz: 'kiedy', naglowek: 'Kiedy', sort: (r) => r.created_at, szer: '170px',
      render: (r) => <span className="tnum whitespace-nowrap">{r.created_at}</span> },
    { klucz: 'rodzaj', naglowek: 'Rodzaj', sort: (r) => r.kind, szer: '140px',
      render: (r) => r.kind },
    { klucz: 'zrodlo', naglowek: 'Źródło',
      render: (r) => (
        <span className="block text-plum-soft truncate max-w-[280px]" title={r.source ?? ''}>
          {r.source ?? '—'}
        </span>
      ) },
    { klucz: 'wczytane', naglowek: 'Wczytane', prawo: true, sort: (r) => r.rows_read, szer: '96px',
      render: (r) => <span className="tnum">{r.rows_read}</span> },
    { klucz: 'zapisane', naglowek: 'Zapisane', prawo: true, sort: (r) => r.rows_added, szer: '96px',
      render: (r) => <span className="tnum">{r.rows_added}</span> },
    { klucz: 'status', naglowek: 'Status', szer: '160px',
      render: (r) => (
        <span>
          <Odznaka ton={r.status === 'ok' ? 'ok' : 'bad'}>{r.status}</Odznaka>
          {r.message && <span className="block text-caption text-plum-muted mt-1">{r.message}</span>}
        </span>
      ) },
  ];

  return (
    <>
      <div className="card p-5">
        <h3 className="text-sub mb-1.5">Skąd biorą się dane</h3>
        <p className="text-body-sm text-plum-soft">
          Numery seryjne i czasy normatywne wjeżdżają z plików na dysku sieciowym.
          Czyta je agent uruchomiony w Waszej sieci
          (<code className="text-caption bg-canvas border border-line rounded-sm px-1 py-0.5">scripts/sync-agent.mjs</code>),
          bo aplikacja w chmurze nie widzi dysku firmowego. Agent pomija import, gdy plik się nie zmienił.
        </p>
      </div>

      <Tabela
        dane={rows}
        kolumny={kolumny}
        klucz={(r) => r.id}
        laduje={laduje}
        pusto={<Pusto tytul="Brak importów"
                      opis="Agent synchronizujący jeszcze nic nie przysłał albo nie został uruchomiony." />}
      />
    </>
  );
}
